class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Cats n Scribbles: an Escape closes the bag only when its keydown began in the bag. Route's 'learn more' window
    // closes itself on keydown and puts focus back in the bag, so the keyup landed here and closed the bag under it
    // (Oct 31 QA). Judged at the keydown: by the keyup Route has moved its window back inside the bag
    // (route-protect-widget, in the footer). A held key's repeats and the Escape that ends an IME composition (the gift
    // note) don't count, and every keyup ends the press, so a lone keyup (its keydown went to the find bar) never
    // closes it
    document.addEventListener(
      'keydown',
      (evt) => {
        if (evt.code !== 'Escape' || evt.repeat) return;
        this.escInBag = !evt.isComposing && evt.keyCode !== 229 && this.contains(evt.target);
      },
      true
    );
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.escInBag && this.close());
    document.addEventListener('keyup', (evt) => evt.code === 'Escape' && (this.escInBag = false));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    // cns: on /cart the bag is the page. The header's bag takes her to its top instead of opening a second copy
    // over it (a change made in that copy left the page showing the old bag).
    if (document.querySelector('cart-items.cns-cart-page')) {
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
      });
      return;
    }

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    // Cats n Scribbles: the page around the bag is inert while it is open, as around the other sheets (global.js), so
    // focus can't walk out to the page behind (Oct 30 QA: with the wallets on, Tab went on from Google Pay to the
    // header's "menu"); Escape closes it wherever focus is, except inside the PayPal iframe, which keeps its keys.
    // A bag line's disclosure open over it closes first, as its own Escape would: with focus on <body> its keyup never
    // fires, and the bag closed under it, leaving the disclosure open over the page with no trap (Oct 30 review)
    cnsInertOutside(this, () => {
      const disclosure = document.querySelector('cart-disclosure-modal[open]');
      disclosure ? disclosure.hide() : this.close();
    });

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    // Cats n Scribbles: without standard-events.js it is a plain element with no view event to fire (cart.js)
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    // Cats n Scribbles: the page comes back first, so focus can return to what opened the bag
    cnsInertRestore(this);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
